import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pipelinesApi } from '../services/pipelinesApi';
import { STAGE_KEYS } from './useStages';
import type {
  CreateTicketPipelineRequest,
  UpdateTicketPipelineRequest,
  TicketPipelineQuery,
  ReorderTicketPipelinesRequest,
  TicketPipelineDeleteValidation
} from '@troia-v3/types';
import { toast } from 'sonner';

export const PIPELINE_KEYS = {
  all: ['ticket-pipelines'] as const,
  lists: () => [...PIPELINE_KEYS.all, 'list'] as const,
  list: (query?: TicketPipelineQuery) => [...PIPELINE_KEYS.lists(), query] as const,
  details: () => [...PIPELINE_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...PIPELINE_KEYS.details(), id] as const,
  dropdown: () => [...PIPELINE_KEYS.all, 'dropdown'] as const,
};

export const usePipelines = (query?: TicketPipelineQuery, enabled: boolean = true) => {
  return useQuery({
    queryKey: PIPELINE_KEYS.list(query),
    queryFn: () => pipelinesApi.getPipelines(query),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
};

/**
 * `enabled` (default `true`) — condicionar a `isOpen` em modais/drawers
 * (CROSS_MODULE_PERMISSIONS) evita disparar a query com o componente fechado.
 */
export const usePipelinesDropdown = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: PIPELINE_KEYS.dropdown(),
    queryFn: () => pipelinesApi.getPipelinesForDropdown(),
    staleTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
};

export const usePipelineStagesDropdown = (pipelineId?: string) => {
  return useQuery({
    queryKey: [...PIPELINE_KEYS.all, 'stages-dropdown', pipelineId],
    queryFn: () => pipelinesApi.getStagesByPipelineId(pipelineId as string),
    enabled: !!pipelineId,
    staleTime: 10 * 60 * 1000,
  });
};

export const useCreatePipeline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTicketPipelineRequest) => pipelinesApi.createPipeline(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEYS.all });
      toast.success('Pipeline criado com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar pipeline');
    },
  });
};

export const useUpdatePipeline = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTicketPipelineRequest }) =>
      pipelinesApi.updatePipeline(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEYS.all });
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEYS.detail(variables.id) });
      // O relógio de nível pipeline (aba "Alertas") também é a fonte do fallback
      // do `resolvedAlert` de cada etapa (cascata etapa → pipeline → sistema,
      // C2/C4) — sem invalidar `STAGE_KEYS`, o card 07 lê o cache de
      // `useStagesByPipelineId` stale por até 5min (staleTime) após salvar aqui.
      queryClient.invalidateQueries({ queryKey: STAGE_KEYS.all });
      toast.success('Pipeline atualizado com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar pipeline');
    },
  });
};

export const useDeletePipelineWithValidation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelinesApi.deletePipelineWithValidation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEYS.all });
      toast.success('Pipeline excluido com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir pipeline');
    },
  });
};

export const useReorderPipelines = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReorderTicketPipelinesRequest) => pipelinesApi.reorderPipelines(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PIPELINE_KEYS.all });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao reordenar pipelines');
    },
  });
};

/**
 * Validação de exclusão sob demanda (clique no ✕ do item da lista).
 *
 * `fetchQuery` em vez de `useQuery` porque a pergunta só existe no momento do
 * clique — montar uma query por item da lista seria N+1 de requisições sem
 * ninguém olhando o resultado. `staleTime: 0` porque validação de exclusão
 * jamais pode vir de cache (mesma escolha de `useValidateDeleteFunnel`).
 *
 * Lança quando a rota falha; o chamador decide o que fazer sem o veredito.
 */
export const usePipelineDeleteValidation = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (id: string): Promise<TicketPipelineDeleteValidation> =>
      queryClient.fetchQuery({
        queryKey: [...PIPELINE_KEYS.detail(id), 'validate-delete'],
        queryFn: () => pipelinesApi.validateDeletePipeline(id),
        staleTime: 0,
      }),
    [queryClient],
  );
};
